import * as THREE from 'three';
import { sampleProfile } from './profileGeometry.js';
import { leafDimensions } from './leafDimensions.js';

// Section through the timber of 40mm_waternose in client/public/models/DoorDynamic.glb.
// Shadow overlay excluded. [Z, height] in mm at the existing exterior mounting plane.
export const WATERNOSE_PROFILE_MM = [
  [28.19,8.08],[28.19,0.08],[-22.21,0.08],[-22.21,-5.92],[-25.2,-5.92],
  [-26.13,-5.77],[-26.96,-5.34],[-27.62,-4.68],[-28.05,-3.84],[-28.19,-2.92],
  [-28.19,6.75],[-28.06,7.64],[-27.66,8.45],[-27.04,9.11],[-26.25,9.56],
  [-25.37,9.74],[-1.69,11.08],[25.2,11.08],[26.13,10.94],[26.96,10.51],
  [27.62,9.85],[28.05,9.01]
].map(([z,y]) => [Number((z - 40).toFixed(2)), Number((y + 5.92).toFixed(2))]);
export const DEFAULT_WATERNOSE = { enabled: false, anchor: 'top', offsetMm: 0, profileMm: WATERNOSE_PROFILE_MM };

export function resolveWaternose(input, heightMm) {
  // Mount flush with the nominal leaf envelope. Old saved offsets cannot move
  // the nose into the fixed assembly clearance.
  const spec = { ...DEFAULT_WATERNOSE, ...input, offsetMm: 0 };
  if (typeof spec.enabled !== 'boolean' || !['top','bottom'].includes(spec.anchor) ||
      !Number.isFinite(spec.offsetMm) || spec.offsetMm < 0 || spec.offsetMm > heightMm) {
    throw new Error('Waternose needs a top/bottom anchor and a valid offset inside the leaf.');
  }
  const profileMm = sampleProfile(spec.profileMm, { closed: true });
  if (profileMm.some(([z,y]) => Math.abs(z) > 300 || y < 0 || y > 150)) throw new Error('Waternose section: Z ±300 mm, height 0–150 mm.');
  const low = Math.min(...profileMm.map(p => p[1])), high = Math.max(...profileMm.map(p => p[1]));
  if (high - low < 1 || high - low + spec.offsetMm > heightMm) throw new Error('Waternose does not fit the leaf height.');
  return { ...spec, profileMm, yMm: spec.anchor === 'top' ? heightMm - spec.offsetMm - high : spec.offsetMm - low };
}

// A top waternose replaces the top strip of the leaf. Find its lowest contact
// with the leaf thickness, excluding the projecting drip edge outside the wood.
// Keep the nominal moving envelope; frame placement supplies the running gap.
export function waternoseLeafTop(spec, heightMm, backMm, frontMm) {
  if (!spec.enabled || spec.anchor !== 'top') return heightMm;
  const heights = [];
  spec.profileMm.forEach((a, i) => {
    const b = spec.profileMm[(i + 1) % spec.profileMm.length];
    if (a[0] >= backMm && a[0] <= frontMm) heights.push(a[1]);
    if (Math.abs(b[0] - a[0]) < 1e-8) return;
    for (const z of [backMm, frontMm]) {
      const t = (z - a[0]) / (b[0] - a[0]);
      if (t >= 0 && t <= 1) heights.push(a[1] + t * (b[1] - a[1]));
    }
  });
  if (!heights.length) throw new Error('The top waternose must overlap the leaf thickness.');
  const top = Math.min(heightMm, spec.yMm + Math.min(...heights));
  if (top <= 80) throw new Error('The waternose must leave more than 80 mm of leaf height.');
  return top;
}

export function leafBodyHeight({ heightMm, thicknessMm, waternose }) {
  if (!waternose?.enabled) return heightMm;
  const { backMm, frontMm } = leafDimensions({ thicknessMm });
  return waternoseLeafTop(resolveWaternose(waternose, heightMm), heightMm, backMm, frontMm);
}

// Owned intermediates use the caller's keep(), including the transformed result.
export function waternoseSolid(Manifold, keep, spec, widthMm) {
  const area = spec.profileMm.reduce((sum,p,i) => { const n = spec.profileMm[(i+1)%spec.profileMm.length]; return sum+p[0]*n[1]-n[0]*p[1]; },0);
  const solid = keep(Manifold.extrude(area > 0 ? spec.profileMm : [...spec.profileMm].reverse(), widthMm));
  const matrix = new THREE.Matrix4().makeBasis(new THREE.Vector3(0,0,1), new THREE.Vector3(0,1,0), new THREE.Vector3(1,0,0));
  matrix.setPosition(0, spec.yMm, 0);
  const result = keep(solid.transform(matrix.elements));
  if (result.status() !== 'NoError' || result.isEmpty()) throw new Error('Invalid waternose profile. Check its points and radii.');
  return result;
}
